/**
 * What a page is told about itself, and what has changed since last time.
 *
 * `docs/thin-core/liquid-page-runtime.md` §1. A surface's page does not hold
 * the lifecycle of anything: the three states of a read, where each submission
 * stands, and whether a draft reached disk are all facts this host already has
 * -- off the same projection the shell's own components read (`commandFeed`,
 * `approvalsFeed`) -- and the page reads them rather than asking again.
 *
 * Two pure pieces live here so they can be tested without a document: the shape
 * one read answer takes in the snapshot, and which facts a new snapshot owes
 * the page. The pushing itself is `SurfaceHost`, which owns the relay.
 */

import type { CommandStatus } from "../lib/api";
import { heldKeys } from "../lib/commandFeed";
import { pluck } from "./spec";

export type ReadState = "pending" | "ready" | "error";

/** One declared read, in exactly one of its three states. */
export interface SurfaceRead {
  index: number;
  tool: string;
  state: ReadState;
  value?: unknown;
  version?: unknown;
  effect_id?: string | null;
  error?: string;
  /** Why this page cannot show it yet, when the answer is `pending`. */
  detail?: string;
}

/** Where one submission of this page stands, keyed by the name the host gave it. */
export interface SurfaceActionState {
  command_id: string;
  obligation_id?: string | null;
  action: string;
  params: Record<string, unknown>;
  object?: unknown;
  state: "submitting" | "received" | "held" | "running" | "completed" | "failed" | "stopped" | "unknown";
  /** The approval cards holding it, as `pa.approvals.open` takes them. */
  held?: string[];
  error?: string | null;
  effects?: CommandStatus["effects"];
  at: number;
}

/** A form's unsaved typing, as the page's runtime stored it (`form.js`). */
export interface SurfaceDraft {
  value?: unknown;
  saved_at?: number;
  /** False the moment the durable write failed; the page must not say 已保存. */
  persisted: boolean;
  /**
   * The durable write is on its way out and has not yet landed or failed.
   * `persisted: false` alone does not distinguish "still writing" from
   * "the write failed" -- a page reading only `persisted` would flash the
   * failure sentence for the length of every round trip. In flight is
   * `{persisted: false, writing: true}`; landed drops both to
   * `{persisted: true}`; failed is `{persisted: false, error}` with no
   * `writing` (`liquid-page-runtime.md` §1).
   */
  writing?: boolean;
  error?: string;
}

export interface SurfaceSnapshot {
  context: Record<string, string>;
  context_revision: number;
  theme: string;
  palette?: string;
  motion?: string;
  size: { width: number; height: number; compact: boolean };
  visible: boolean;
  /** A card's page only: the record of the call it sits under (`records.tsx`). */
  record?: unknown;
  reads: SurfaceRead[];
  actions: Record<string, SurfaceActionState>;
  drafts: Record<string, SurfaceDraft>;
}

/** One answer from `surface.tail`, in the shape the snapshot is read in. */
export function readFromAnswer(index: number, answer: Record<string, unknown>, versionPath: string): SurfaceRead | null {
  const state = String(answer.state || "");
  const tool = String(answer.tool || "");
  const effect = (answer.effect_id as string | null) ?? null;
  // A read this call did not ask for says nothing about the read: the entry
  // that is already on the page stays.
  if (state === "skipped") return null;
  if (state === "error") return { index, tool, state: "error", effect_id: effect, error: String(answer.error || "") };
  if (state !== "ready") return { index, tool, state: "pending", detail: String(answer.detail || "") };
  const rows = answer.rows;
  const value = rows === undefined || rows === null ? answer.value : rows;
  return {
    index, tool, state: "ready", effect_id: effect, value,
    // The version is the plugin's declaration, read out of the value here so
    // that no page has to invent one (§3).
    ...(versionPath ? { version: pluck(value, versionPath) } : {}),
  };
}

/** Where one press stands: what the page sent under its name, and what the projection says of it. */
export function actionFromPress(
  commandId: string,
  press: { params: Record<string, unknown>; obligation_id?: string; unknown?: boolean; at: number },
  status: CommandStatus | undefined,
): SurfaceActionState {
  const known = status?.known ? status.action_state : undefined;
  return {
    command_id: commandId,
    obligation_id: press.obligation_id ?? null,
    action: String(press.params.tool || ""),
    params: (press.params.arguments as Record<string, unknown>) || {},
    object: press.params.object,
    // `received` is the core having taken it; it is not `running` and it is
    // certainly not `completed` (§2 rule 3). `unknown` is an answer that did
    // not come back: sending the same press again finds out.
    state: known ?? (press.obligation_id ? "received" : press.unknown ? "unknown" : "submitting"),
    held: status ? heldKeys(status) : [],
    error: status?.error ?? null,
    effects: status?.effects ?? [],
    at: press.at,
  };
}

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

/**
 * The frame a new snapshot owes the page: the facts that actually moved.
 *
 * The same rule `commands.py::frame_signature` keeps on the server -- one
 * message per change, never one per event -- so a page that redraws on every
 * frame redraws when something happened and not otherwise. `null` when nothing
 * moved at all.
 */
export function stateFrame(
  previous: SurfaceSnapshot | null, next: SurfaceSnapshot,
): Partial<SurfaceSnapshot> & { full?: boolean; context_revision: number } | null {
  if (!previous) return { ...next, full: true };
  const frame: Record<string, unknown> = { context_revision: next.context_revision };
  let moved = false;
  for (const fact of ["context", "theme", "palette", "size", "visible", "record", "reads"] as const) {
    if (!same(previous[fact], next[fact])) {
      frame[fact] = next[fact];
      moved = true;
    }
  }
  for (const fact of ["actions", "drafts"] as const) {
    const changes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(next[fact])) {
      if (!same((previous[fact] as Record<string, unknown>)[key], value)) changes[key] = value;
    }
    for (const key of Object.keys(previous[fact])) {
      if (!(key in next[fact])) changes[key] = null;
    }
    if (Object.keys(changes).length) {
      frame[fact] = changes;
      moved = true;
    }
  }
  if (!moved && previous.context_revision === next.context_revision) return null;
  return frame as Partial<SurfaceSnapshot> & { full?: boolean; context_revision: number };
}
